import { useOrganization } from '@clerk/clerk-react';
import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { FranchiseesTable } from '@/components/tables/FranchiseesTable';
import { LeadershipTeamTable } from '@/components/tables/LeadershipTeamTable';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ContactDataType } from '@/constants/contacts/dummy';
import { ORG_RESTRICTIONS_ZEE_LIST } from '@/constants/organizations';
import { useGetAllFranchisees } from '@/hooks/franchisees/useGetAllFranchisees';
import { toast } from '@/hooks/ui/use-toast';
import { useGetAllZorMembers } from '@/hooks/zor-member/useGetAllZorMembers';
import { ZorMember } from '@/types/zor.member.type';

type TabValue = 'leadership' | 'franchisees';

const TAB_OPTIONS: { value: TabValue; label: string }[] = [
  { value: 'leadership', label: 'Leadership' },
  { value: 'franchisees', label: 'Franchisees' },
];

const LEADERSHIP_LIMIT_STORAGE_KEY = 'consultant_leadership_table_page_limit';
const FRANCHISEES_LIMIT_STORAGE_KEY = 'consultant_franchisees_table_page_limit';
const DEFAULT_LIMIT = 10;

const getStoredLimit = (key: string): number => {
  try {
    const stored = localStorage.getItem(key);
    const parsed = stored ? parseInt(stored, 10) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_LIMIT;
  } catch {
    return DEFAULT_LIMIT;
  }
};

const FranchiseContactsTab = () => {
  const { organization } = useOrganization();

  const [leadershipPage, setLeadershipPage] = useState(1);
  const [leadershipLimit, setLeadershipLimit] = useState(() =>
    getStoredLimit(LEADERSHIP_LIMIT_STORAGE_KEY)
  );
  const [franchiseesPage, setFranchiseesPage] = useState(1);
  const [franchiseesLimit, setFranchiseesLimit] = useState(() =>
    getStoredLimit(FRANCHISEES_LIMIT_STORAGE_KEY)
  );

  const {
    result: franchiseesFromApi,
    isError: isFranchiseesError,
    isLoading: isFranchiseesLoading,
    isRefetching: isFranchiseesRefetching,
  } = useGetAllFranchisees({ page: franchiseesPage, limit: franchiseesLimit });

  const {
    result: zorMembersFromApi,
    isError: isZorMembersError,
    isLoading: isZorMembersLoading,
    isRefetching: isZorMembersRefetching,
  } = useGetAllZorMembers({ page: leadershipPage, limit: leadershipLimit });

  const isZeeList = ORG_RESTRICTIONS_ZEE_LIST.includes(
    organization?.name || ''
  );

  useEffect(() => {
    if (isFranchiseesError || isZorMembersError) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again later',
        variant: 'destructive',
      });
    }
  }, [isFranchiseesError, isZorMembersError]);

  const leadershipData = useMemo(
    () =>
      (zorMembersFromApi?.data?.items ?? []).map(
        (m: ZorMember): ContactDataType => ({
          id: m.id,
          full_name: m.full_name,
          photo_url: m.photo_url,
          position: m.position,
          email: m.email,
          linkedin: m.linkedin,
          meeting_link: m.meeting_link,
        })
      ),
    [zorMembersFromApi]
  );

  const franchiseesData = useMemo(
    () => franchiseesFromApi?.data?.items ?? [],
    [franchiseesFromApi]
  );

  const handleLeadershipLimitChange = (newLimit: number) => {
    setLeadershipLimit(newLimit);
    setLeadershipPage(1);
    try {
      localStorage.setItem(LEADERSHIP_LIMIT_STORAGE_KEY, String(newLimit));
    } catch {
      // ignore storage errors
    }
  };

  const handleFranchiseesLimitChange = (newLimit: number) => {
    setFranchiseesLimit(newLimit);
    setFranchiseesPage(1);
    try {
      localStorage.setItem(FRANCHISEES_LIMIT_STORAGE_KEY, String(newLimit));
    } catch {
      // ignore storage errors
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center gap-2 border-b border-border pb-2">
        <p className="as-title-01 as-strong text-zinc-700">Contacts</p>
        {(isFranchiseesRefetching || isZorMembersRefetching) && (
          <Loader2 className="size-4 animate-spin" />
        )}
      </div>
      <div className="rounded-xl md:bg-white md:p-4">
        <Tabs defaultValue="leadership">
          <div className="flex items-center justify-between gap-2 max-md:flex-col md:flex-wrap">
            <TabsList className="w-full max-md:order-2 max-md:bg-slate-200 md:w-fit">
              {TAB_OPTIONS.map(({ value, label }) => (
                <TabsTrigger key={value} value={value} className="w-full">
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          {isZorMembersLoading || isFranchiseesLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="size-4 animate-spin" />
            </div>
          ) : (
            <>
              <TabsContent value="leadership">
                <LeadershipTeamTable
                  data={leadershipData}
                  page={leadershipPage}
                  totalPages={zorMembersFromApi?.data?.total_pages ?? 1}
                  total={zorMembersFromApi?.data?.total ?? 0}
                  limit={leadershipLimit}
                  onPageChange={setLeadershipPage}
                  onLimitChange={handleLeadershipLimitChange}
                />
              </TabsContent>
              <TabsContent value="franchisees">
                <FranchiseesTable
                  data={isZeeList ? [] : franchiseesData}
                  page={franchiseesPage}
                  totalPages={
                    isZeeList ? 1 : (franchiseesFromApi?.data?.total_pages ?? 1)
                  }
                  total={isZeeList ? 0 : (franchiseesFromApi?.data?.total ?? 0)}
                  limit={franchiseesLimit}
                  onPageChange={setFranchiseesPage}
                  onLimitChange={handleFranchiseesLimitChange}
                />
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export { FranchiseContactsTab };
